import type { LatestEarnings } from "@/domain/gems-game/domain";
import { useConnection } from "@/hooks/connection";
import { trackLegacyEvent } from "@analytics";
import { ShimmerBox } from "@biom3/react";
import { Button } from "@biom3/react";
import { Box } from "@biom3/react";
import type { WalletGems } from "@imx-tokens-ts/gems-game/data-access";
import { useCallback, useState } from "react";
import { WalletWidget } from "../../wallet/WalletWidget";
import { GemsClaimCta } from "./GemsClaimCta";
import { GemsClaimed } from "./GemsClaimed";

type GemsClaimProps = {
  walletAddress: string | null;
  walletGems: WalletGems | undefined;
  canClaim: boolean;
  hasJustEarned: boolean;
  onGemEarned: (walletGems: WalletGems) => void;
  latestEarnings: LatestEarnings | undefined;
  onUTCDayChange: () => void;
  disconnect: () => Promise<void>;
  isPassportWallet: boolean;
};

function ConnectToClaim() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isConnecting, setIsConnecting } = useConnection();

  const connect = () => {
    setIsModalOpen(true);
    setIsConnecting(true);
    trackLegacyEvent({
      control: "ConnectToClaim",
      controlType: "Button",
      screen: "Claim",
      userJourney: "Claim",
      extras: {
        variableGemsEnabled: true,
      },
    });
  };

  const onClose = useCallback(() => {
    setIsModalOpen(false);
    setIsConnecting(false);
  }, [setIsConnecting]);

  return (
    <>
      <WalletWidget isModalOpen={isModalOpen} onClose={onClose} />
      <Button
        size="large"
        onClick={connect}
        variant="secondary"
        disabled={isConnecting}
        testId="claim-gems__connect-btn"
        sx={{
          width: "220px",
        }}
      >
        {isConnecting ? "Connecting wallet" : "Connect to claim"}
        <Button.Icon icon={isConnecting ? "Loading" : "Wallet"} />
      </Button>
    </>
  );
}

function LoadingGems() {
  return (
    <ShimmerBox
      testId="loading-gems"
      sx={{
        height: "64px",
        width: "220px",
      }}
    />
  );
}

export function GemsClaim({
  walletAddress,
  walletGems,
  canClaim,
  onGemEarned,
  hasJustEarned,
  latestEarnings,
  onUTCDayChange,
  disconnect,
  isPassportWallet,
}: GemsClaimProps) {
  return (
    <Box sx={{ minWidth: "220px", py: "base.spacing.x6" }}>
      {!walletAddress || !walletGems ? (
        <ConnectToClaim />
      ) : !latestEarnings ? (
        <LoadingGems />
      ) : canClaim || hasJustEarned ? (
        <GemsClaimCta
          walletGems={walletGems}
          onGemEarned={onGemEarned}
          hasJustEarned={hasJustEarned}
          disconnect={disconnect}
          isPassportWallet={isPassportWallet}
        />
      ) : (
        <GemsClaimed
          onUTCDayChange={onUTCDayChange}
          walletAddress={walletAddress}
          disconnect={disconnect}
          isPassportWallet={isPassportWallet}
        />
      )}
    </Box>
  );
}
